import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Music, Upload as UploadIcon, Loader2, Trash2, FileDown, Plus, X, Percent, CheckCircle2 } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { logActivity } from "../lib/driveClient";
import { fetchArtists } from "../lib/driveClient";
import { CreateModal as ProvisionCreateModal } from "./DriveProvisions";

function formatEUR(v) {
  return (Number(v || 0)).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

async function fetchPayouts() {
  const { data, error } = await supabase
    .from("drive_music_hub_payouts")
    .select("*")
    .order("month", { ascending: false })
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

async function uploadPayout({ month, title, amount, file, notes }) {
  const safe = file.name.replace(/[^a-zA-Z0-9._-]+/g, "_");
  const path = `music-hub/${month}/${Date.now()}-${safe}`;
  const up = await supabase.storage.from("drive-files").upload(path, file, { contentType: file.type || "application/pdf" });
  if (up.error) throw up.error;

  const { data, error } = await supabase.from("drive_music_hub_payouts").insert([{
    month, title, amount: Number(amount) || 0, file_name: file.name, storage_path: path, notes,
  }]).select().single();
  if (error) throw error;
  await logActivity("upload_musichub", { type: "musichub", id: data.id, name: title });
  return data;
}

async function deletePayout(row) {
  if (row.storage_path) {
    await supabase.storage.from("drive-files").remove([row.storage_path]).catch(() => {});
  }
  const { error } = await supabase.from("drive_music_hub_payouts").delete().eq("id", row.id);
  if (error) throw error;
  await logActivity("delete_musichub", { type: "musichub", id: row.id, name: row.title });
}

async function downloadFile(row) {
  const { data: blob, error } = await supabase.storage.from("drive-files").download(row.storage_path);
  if (error) throw error;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = row.file_name || `MusicHub-${row.month}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export default function DriveMusicHub() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [provisionFor, setProvisionFor] = useState(null); // Payout-Row für Provisions-Modal
  const [artists, setArtists] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, a, provs] = await Promise.all([
        fetchPayouts(),
        fetchArtists().catch(() => []),
        supabase.from("drive_provision_statements").select("musichub_id").then((r) => r.data || []),
      ]);
      // Set of MusicHub-IDs die bereits abgerechnet wurden
      const billedSet = new Set(provs.filter(x => x.musichub_id).map(x => x.musichub_id));
      const withStatus = p.map(row => ({ ...row, _billed: billedSet.has(row.id) }));
      setRows(withStatus);
      setArtists(a);
    }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Gruppiert nach Monat
  const grouped = useMemo(() => {
    const g = {};
    for (const r of rows) {
      (g[r.month] ||= []).push(r);
    }
    return g;
  }, [rows]);

  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);

  const handleDelete = async (row) => {
    if (!window.confirm(`Abrechnung „${row.title || row.file_name}" wirklich löschen?`)) return;
    setBusy(`del-${row.id}`);
    try { await deletePayout(row); toast.success("Gelöscht"); await load(); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  const handleDownload = async (row) => {
    setBusy(`dl-${row.id}`);
    try { await downloadFile(row); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="text-[10px] tracking-[0.35em] uppercase text-[#ff0033] font-bold mb-3">Buchhaltung</div>
        <div className="font-display font-black uppercase text-3xl md:text-4xl mb-2" data-testid="musichub-title">
          MusicHub Abrechnungen
        </div>
        <div className="text-sm text-zinc-400 max-w-2xl">
          Monatliche Auszahlungsabrechnungen von MusicHub. Werden automatisch im Monatsabschluss mit einbezogen und in die zusammengesetzte PDF integriert.
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-5 bg-[#0A0A0A] border border-white/[0.06]">
          <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">Abrechnungen</div>
          <div className="font-display font-black text-2xl text-white">{rows.length}</div>
        </div>
        <div className="p-5 bg-[#0A0A0A] border border-white/[0.06]">
          <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">Monate</div>
          <div className="font-display font-black text-2xl text-white">{Object.keys(grouped).length}</div>
        </div>
        <div className="p-5 bg-[#ff0033]/[0.06] border border-[#ff0033]/30">
          <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">Gesamt-Auszahlungen</div>
          <div className="font-display font-black text-2xl text-[#ff0033]">{formatEUR(total)}</div>
        </div>
      </div>

      {/* Actions */}
      <div>
        <button
          onClick={() => setShowModal(true)}
          data-testid="add-musichub"
          className="inline-flex items-center gap-3 bg-white text-black hover:bg-[#ff0033] hover:text-white px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase transition-colors"
        >
          <Plus size={14} /> Neue Abrechnung hochladen
        </button>
      </div>

      {/* List by month */}
      {loading ? (
        <div className="flex items-center gap-3 text-zinc-400 text-sm"><Loader2 size={16} className="animate-spin" /> Lade…</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="bg-[#0A0A0A] border border-white/[0.06] px-6 py-10 text-center text-sm text-zinc-500 italic">
          Noch keine MusicHub-Abrechnungen. Klick oben auf „Neue Abrechnung hochladen" um zu starten.
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).sort(([a], [b]) => b.localeCompare(a)).map(([month, items]) => {
            const [yr, mo] = month.split("-");
            const label = new Date(Number(yr), Number(mo) - 1, 1).toLocaleDateString("de-DE", { month: "long", year: "numeric" });
            const monthTotal = items.reduce((s, r) => s + Number(r.amount || 0), 0);
            return (
              <div key={month} className="bg-[#0A0A0A] border border-white/[0.06]" data-testid={`month-group-${month}`}>
                <div className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between">
                  <div className="font-display font-black uppercase text-lg text-white">{label}</div>
                  <div className="flex items-center gap-4">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">{items.length} × Abrechnungen</div>
                    <div className="text-sm text-[#ff0033] font-bold tabular-nums">{formatEUR(monthTotal)}</div>
                  </div>
                </div>
                <div className="divide-y divide-white/[0.04]">
                  {items.map((r) => (
                    <div key={r.id} className="grid grid-cols-1 md:grid-cols-[1fr_140px_auto] gap-3 items-center px-5 py-3">
                      <div className="min-w-0">
                        <div className="text-sm text-white font-medium truncate">{r.title || r.file_name}</div>
                        {r.notes && <div className="text-xs text-zinc-500 mt-0.5 truncate">{r.notes}</div>}
                        <div className="text-[10px] text-zinc-600 mt-1 tracking-wide">Hochgeladen: {new Date(r.created_at).toLocaleDateString("de-DE")}</div>
                      </div>
                      <div className="text-sm text-white tabular-nums">{formatEUR(r.amount)}</div>
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => !r._billed && setProvisionFor(r)}
                          disabled={r._billed}
                          data-testid={`prov-${r.id}`}
                          title={r._billed ? "Bereits abgerechnet" : "Provisionsabrechnung erstellen"}
                          className={`inline-flex items-center gap-2 px-3 py-2 text-[9px] font-bold tracking-[0.25em] uppercase transition-colors border ${r._billed ? "border-emerald-500/40 text-emerald-400 cursor-default" : "border-[#ff0033]/40 text-[#ff0033] hover:bg-[#ff0033] hover:text-white"}`}
                        >
                          {r._billed ? <><CheckCircle2 size={11} /> Abgerechnet</> : <><Percent size={11} /> Abrechnen</>}
                        </button>
                        <button
                          onClick={() => handleDownload(r)}
                          disabled={busy === `dl-${r.id}`}
                          data-testid={`dl-${r.id}`}
                          title="Herunterladen"
                          className="inline-flex items-center justify-center p-2 border border-white/15 hover:border-[#ff0033]/50 text-zinc-300 hover:text-white transition-colors disabled:opacity-40"
                        >
                          {busy === `dl-${r.id}` ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />}
                        </button>
                        <button
                          onClick={() => handleDelete(r)}
                          disabled={busy === `del-${r.id}`}
                          data-testid={`del-${r.id}`}
                          title="Löschen"
                          className="inline-flex items-center justify-center p-2 border border-white/10 text-zinc-500 hover:border-[#ff0033]/50 hover:text-[#ff0033] transition-colors disabled:opacity-40"
                        >
                          {busy === `del-${r.id}` ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && <UploadModal onClose={() => setShowModal(false)} onDone={async () => { setShowModal(false); await load(); }} />}
      {provisionFor && (
        <ProvisionCreateModal
          artists={artists}
          payouts={rows}
          prefillPayout={provisionFor}
          onClose={() => setProvisionFor(null)}
          onDone={() => { setProvisionFor(null); toast.success('Provisionsabrechnung erstellt — findest du unter "Provisionen".'); }}
        />
      )}
    </div>
  );
}

const UploadModal = ({ onClose, onDone }) => {
  const [month, setMonth] = useState(currentMonth());
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) { toast.error("Bitte Datei auswählen"); return; }
    if (!month) { toast.error("Bitte Monat wählen"); return; }
    setBusy(true);
    try {
      await uploadPayout({ month, title: title || `MusicHub ${month}`, amount, file, notes });
      toast.success("Hochgeladen");
      onDone();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A] border border-white/[0.08] max-w-lg w-full">
        <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#ff0033] font-bold">MusicHub</div>
            <div className="font-display font-black uppercase text-lg mt-1">Neue Abrechnung</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">Monat</label>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="mh-month" className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white outline-none focus:border-[#ff0033]/60" />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">Bezeichnung (optional)</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={`MusicHub ${month}`} data-testid="mh-title" className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white outline-none focus:border-[#ff0033]/60" />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">Auszahlungsbetrag (€)</label>
            <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0,00" data-testid="mh-amount" className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white outline-none focus:border-[#ff0033]/60" />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">Notizen</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="mh-notes" className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white outline-none focus:border-[#ff0033]/60" />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">PDF-Datei</label>
            <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="mh-file" className="w-full text-sm text-zinc-300" />
            {file && <div className="text-[10px] text-zinc-500 mt-1 tracking-wide">{file.name} · {(file.size / 1024).toFixed(0)} KB</div>}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-white/[0.06] flex items-center justify-end gap-3">
          <button onClick={onClose} className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-500 hover:text-white">Abbrechen</button>
          <button onClick={submit} disabled={busy} data-testid="mh-submit" className="inline-flex items-center gap-2 bg-[#ff0033] text-white px-5 py-3 text-[10px] font-bold tracking-[0.3em] uppercase hover:shadow-[0_0_20px_rgba(255,0,51,0.5)] transition-all disabled:opacity-50">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <UploadIcon size={13} />}
            {busy ? "Lädt hoch…" : "Hochladen"}
          </button>
        </div>
      </div>
    </div>
  );
};
